import { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { message } from "antd";
import { FIRESTORE_PRODUCTS_STOCK_TABLE, FIRESTORE_REGIONS_TABLE } from "constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "constants/CompanyConstants";

/**
 * Hook to fetch stock data for reports with a dynamic date range
 * @param {Array} dateRange - [startDate, endDate] as dayjs objects or null
 * @returns {Object} { stockData, loading, error }
 */
export const useStockReport = (dateRange) => {
  const [stockData, setStockData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStockData = async () => {
      if (!dateRange || !dateRange[0] || !dateRange[1]) {
        setStockData({});
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const startDate = dayjs(dateRange[0]).startOf("day").unix();
        const endDate = dayjs(dateRange[1]).endOf("day").unix();

        const stockQuery = query(
          collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_STOCK_TABLE),
          where("dateUnix", ">=", startDate),
          where("dateUnix", "<=", endDate),
          orderBy("dateUnix", "asc"),
        );

        const snapshot = await getDocs(stockQuery);
        const stockByDate = {};

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.products) {
            stockByDate[doc.id] = {
              date: data.date,
              dateUnix: data.dateUnix,
              products: data.products,
            };
          }
        });

        setStockData(stockByDate);
      } catch (err) {
        console.error("Error fetching stock report data:", err);
        setError(err);
        message.error("Failed to fetch stock data for report");
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();
  }, [dateRange?.[0]?.valueOf(), dateRange?.[1]?.valueOf()]);

  /**
   * Get aggregated addedStock per product for the date range
   * @param {string} companyFilter - "All", "CF", or "GR"
   * @param {Array} products - Array of product objects with id and company fields
   * @returns {Array} Sorted array of { productId, productName, totalAddedStock }
   */
  const getAggregatedAddedStock = (companyFilter = "All", products = []) => {
    const productTotals = {};

    // Aggregate addedStock across all dates
    Object.values(stockData).forEach((dateData) => {
      if (dateData.products) {
        Object.entries(dateData.products).forEach(([productId, productData]) => {
          if (productData.addedStock && productData.addedStock > 0) {
            // Apply company filter
            const product = products.find((p) => p.id === productId);
            if (companyFilter !== "All") {
              if (!product || product.company !== companyFilter) {
                return;
              }
            }

            if (productTotals[productId]) {
              productTotals[productId].totalAddedStock += productData.addedStock;
            } else {
              productTotals[productId] = {
                productId,
                productName: product?.productName || product?.name?.en || productId,
                totalAddedStock: productData.addedStock,
              };
            }
          }
        });
      }
    });

    // Convert to array and sort by totalAddedStock descending
    return Object.values(productTotals).sort((a, b) => b.totalAddedStock - a.totalAddedStock);
  };

  /**
   * Get stock data formatted for Excel export
   * @param {string} companyFilter - "All", "CF", or "GR"
   * @param {Array} products - Array of product objects with id and company fields
   * @returns {Object} { dates: string[], products: Array<{ productId, productName, total, stockByDate: {} }> }
   */
  const getStockDataForExport = (companyFilter = "All", products = []) => {
    const productTotals = {};
    const allDates = new Set();

    // Collect all dates and aggregate stock data
    Object.entries(stockData).forEach(([dateKey, dateData]) => {
      if (dateData.products && dateData.date) {
        allDates.add(dateData.date);

        Object.entries(dateData.products).forEach(([productId, productData]) => {
          if (productData.addedStock && productData.addedStock > 0) {
            // Apply company filter
            const product = products.find((p) => p.id === productId);
            if (companyFilter !== "All") {
              if (!product || product.company !== companyFilter) {
                return;
              }
            }

            if (!productTotals[productId]) {
              productTotals[productId] = {
                productId,
                productName: product?.productName || product?.name?.en || productId,
                total: 0,
                stockByDate: {},
              };
            }

            productTotals[productId].total += productData.addedStock;
            productTotals[productId].stockByDate[dateData.date] = productData.addedStock;
          }
        });
      }
    });

    // Sort dates chronologically (DD-MM-YYYY format)
    const sortedDates = Array.from(allDates).sort((a, b) => {
      const [dayA, monthA, yearA] = a.split("-").map(Number);
      const [dayB, monthB, yearB] = b.split("-").map(Number);
      return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
    });

    // Sort products by total descending
    const sortedProducts = Object.values(productTotals).sort((a, b) => b.total - a.total);

    return {
      dates: sortedDates,
      products: sortedProducts,
    };
  };

  return {
    stockData,
    loading,
    error,
    getAggregatedAddedStock,
    getStockDataForExport,
  };
};

export default useStockReport;
